/**
 * Keeping the focused element on screen (`03-client-app.md` section 3.2).
 *
 * One press deserves a smooth scroll. A held direction does not: each move starts another
 * animation over the top of the one still running, and because each new one restarts from a
 * standstill, the artwork falls behind the ring and then lurches to catch up. Holding right for
 * a second turns into a row that stutters and a ring that is somewhere off the edge.
 *
 * So the rule is: the first move of a press eases, and everything that lands while the screen is
 * still travelling is taken instantly. The ring stays glued to the tile, and the row moves at
 * the rate the thumb is asking for rather than at the rate an easing curve wants.
 */

/** A move landing within this of the last one is part of the same journey, not a new one. */
const SETTLE_MS = 260;
/** How long a requested alignment stays on offer before it is treated as stale. */
const HINT_MS = 300;

let lastReveal = 0;
let hint: { block: ScrollLogicalPosition; at: number } | null = null;

/**
 * How the next element to take focus should be brought into view.
 *
 * Only paging asks. A page down landing on `nearest` would bring the tile to rest against the
 * bottom edge — which is roughly where it already was — so the ring would travel a screenful
 * through the document while the screen barely moved. A page puts what it landed on at the
 * leading edge, so the screenful in front of it is the one you have not read yet.
 */
export function revealNextAs(block: ScrollLogicalPosition): void {
  hint = { block, at: performance.now() };
}

export function revealFocused(node: HTMLElement): void {
  const now = performance.now();
  const travelling = now - lastReveal < SETTLE_MS;
  lastReveal = now;

  // A hint is for the one move that asked for it. If focus went somewhere else first, or nothing
  // took it up in time, it is dropped rather than left to surprise a later move.
  const block = hint && now - hint.at < HINT_MS ? hint.block : 'nearest';
  hint = null;

  // `nearest` by default on both axes, so an element already on screen is left exactly where it
  // is and the page does not shuffle under someone who is only looking around. How much room is
  // left at the edges is the scroll area's business, and it says so with `scroll-padding`.
  node.scrollIntoView({
    block,
    inline: 'nearest',
    behavior: travelling || motionIsOff() ? 'auto' : 'smooth',
  });
}

/**
 * Whether animation is turned off, by the platform or by the app's own switch.
 *
 * The stylesheet cannot answer this one. `scroll-behavior: auto` only decides what an unspecified
 * scroll does, and an explicit `behavior` on `scrollIntoView` overrides it, so a reduced-motion
 * setting has to be read here or it is quietly ignored.
 */
function motionIsOff(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.dataset.reduceMotion === 'true') return true;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}
